import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { DireccionDetalles, DireccionValidacion } from '../../types';
import { LoadingSpinner } from './LoadingSpinner';
import { normalizarDireccion } from '../../utils/direccionUtils';
import { useGoogleMaps } from './hooks/useGoogleMaps';

// ✅ Utilidad para debouncing
function debounce(func: (input: string) => void, wait: number): (input: string) => void {
  let timeout: NodeJS.Timeout;
  return (input: string) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(input), wait);
  };
}

interface DireccionInputProps {
  value: string;
  onChange: (detalles: DireccionDetalles) => void;
  onValidation?: (validacion: DireccionValidacion) => void;
  onTextChange?: (text: string) => void;
  className?: string;
  placeholder?: string;
}

export const DireccionInput: React.FC<DireccionInputProps> = ({
  value,
  onChange,
  onValidation,
  onTextChange,
  className = '',
  placeholder = 'Dirección completa'
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

  const [inputValue, setInputValue] = useState(value);
  const [hasSelectedPlace, setHasSelectedPlace] = useState(!!value);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // ✅ Estados para manejo de gestos táctiles
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const touchStartTimeRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { isLoaded, loadError } = useGoogleMaps();

  const stableOnChange = useCallback((detalles: DireccionDetalles) => {
    onChange(detalles);
  }, [onChange]);

  const stableOnValidation = useCallback((validacion: DireccionValidacion) => {
    onValidation?.(validacion);
  }, [onValidation]);

  // ✅ Inicializar servicios de Google Places
  useEffect(() => {
    if (isLoaded) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      const mapDiv = document.createElement('div');
      placesServiceRef.current = new google.maps.places.PlacesService(mapDiv);
    }
  }, [isLoaded]);

  // ✅ Cleanup de timeouts
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // ✅ Obtener sugerencias con debouncing
  const debouncedGetSuggestions = useCallback(
    (input: string) => {
      if (input.length > 2 && autocompleteServiceRef.current) {
        autocompleteServiceRef.current.getPlacePredictions(
          { input, types: ['address'] },
          (predictions, status) => {
            if (status === 'OK' && predictions) {
              setSuggestions(predictions);
              setShowSuggestions(true);
            } else {
              setShowSuggestions(false);
            }
          }
        );
      } else {
        setShowSuggestions(false);
      }
    },
    []
  );

  // ✅ Aplicar debouncing
  const debouncedGetSuggestionsWithDelay = useMemo(
    () => debounce(debouncedGetSuggestions, 300),
    [debouncedGetSuggestions]
  );


  // ✅ Manejar cambios en el input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (onTextChange) {
      onTextChange(newValue);
    }

    if (hasSelectedPlace) {
      setHasSelectedPlace(false);
    }

    // Obtener sugerencias con debouncing
    debouncedGetSuggestionsWithDelay(newValue);

    // VALIDACIÓN: Verificar si la dirección parece incompleta
    if (newValue.trim() && !hasSelectedPlace) {
      const tieneInformacionGeo = /\b(ciudad|provincia|estado|departamento|región|país|country|state|city|province|department|region)\b/i.test(newValue) ||
                                newValue.includes(',') && newValue.split(',').length >= 2;

      if (!tieneInformacionGeo && newValue.length > 5) {
        onValidation?.({
          isValid: false,
          error: 'Para una mejor geolocalización, selecciona una dirección completa de la lista o agrega ciudad y país'
        });
      }
    }
  };

  // ✅ Manejar selección directa de sugerencia
  const handleSuggestionClick = async (prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesServiceRef.current) return;

    setLoading(true);
    setShowSuggestions(false);

    placesServiceRef.current.getDetails(
      { placeId: prediction.place_id },
      (place, status) => {
        if (status === 'OK' && place) {
          const formatted = place.formatted_address || prediction.description;
          setInputValue(formatted);
          setHasSelectedPlace(true);

          // Procesar la dirección seleccionada
          const hasGeo = !!place?.geometry?.location;
          const comps = place?.address_components || [];

          let lat: number | undefined;
          let lng: number | undefined;

          if (hasGeo && place.geometry?.location) {
            const loc = place.geometry.location;
            lat = typeof loc.lat === 'function' ? loc.lat() : Number(loc.lat);
            lng = typeof loc.lng === 'function' ? loc.lng() : Number(loc.lng);
          }

          const direccionDetalles: DireccionDetalles = {
            placeId: place?.place_id || '',
            direccionCompleta: formatted,
            direccionNormalizada: normalizarDireccion(formatted),
            calle: comps.find(c => c.types.includes('route'))?.long_name,
            numero: comps.find(c => c.types.includes('street_number'))?.long_name,
            colonia: comps.find(c => c.types.includes('sublocality'))?.long_name,
            ciudad: comps.find(c => c.types.includes('locality'))?.long_name
                 || comps.find(c => c.types.includes('administrative_area_level_2'))?.long_name,
            estado: comps.find(c => c.types.includes('administrative_area_level_1'))?.long_name,
            codigoPostal: comps.find(c => c.types.includes('postal_code'))?.long_name,
            pais: comps.find(c => c.types.includes('country'))?.long_name || 'Unknown',
            coords: (lat !== undefined && lng !== undefined) ? { lat, lng } : { lat: 0, lng: 0 },
            addressComponents: comps.map(c => ({
              long_name: c.long_name || '',
              short_name: c.short_name || '',
              types: c.types || []
            }))
          };

          stableOnChange(direccionDetalles);
          stableOnValidation?.({ isValid: true });
        }
        setLoading(false);
      }
    );
  };

  // ✅ Manejar eventos táctiles para distinguir entre scroll y tap
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setTouchMoved(false);
    touchStartTimeRef.current = Date.now();
  };

  const handleTouchMove = () => {
    setTouchMoved(true);
    setIsScrolling(true);
    
    // Limpiar timeout anterior
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Marcar que estamos scrolleando por un tiempo corto
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  const handleTouchEnd = (e: React.TouchEvent, prediction: google.maps.places.AutocompletePrediction) => {
    const touchDuration = Date.now() - touchStartTimeRef.current;
    const touchEnd = e.changedTouches[0];
    
    // Solo seleccionar si:
    // 1. No hubo movimiento significativo (no es scroll)
    // 2. El toque fue breve (< 400ms) - más tiempo para permitir gestos
    // 3. El movimiento vertical fue mínimo (< 15px) - más tolerancia
    const verticalMovement = touchStartY !== null ? Math.abs(touchEnd.clientY - touchStartY) : 0;
    
    // Solo seleccionar si no hubo scroll y cumple los criterios de tap
    if (!touchMoved && !isScrolling && touchDuration < 400 && verticalMovement < 15) {
      handleSuggestionClick(prediction);
    }
    
    // Resetear estados
    setTouchStartY(null);
    setTouchMoved(false);
  };


  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        className={className}
        placeholder={loadError ? 'Error al cargar direcciones' : placeholder}
        disabled={!isLoaded || loading || !!loadError}
        // ✅ Atributos mobile-friendly
        inputMode="text"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        style={{ fontSize: 16 }}
      />

      {/* ✅ Dropdown propio con control total de eventos */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
          style={{ 
            // ✅ Mejorar área táctil en móvil
            minHeight: '120px',
            touchAction: 'pan-y'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        >
          {suggestions.map((prediction) => (
            <div
              key={prediction.place_id}
              className="p-4 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 select-none"
              style={{
                // ✅ Área táctil más amplia
                minHeight: '48px',
                display: 'flex',
                alignItems: 'center'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(prediction);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                handleTouchEnd(e, prediction);
              }}
              onClick={(e) => {
                e.preventDefault();
                // Fallback para dispositivos que no soportan touch events
                if (navigator.maxTouchPoints === 0) {
                  handleSuggestionClick(prediction);
                }
              }}
            >
              <span className="text-sm leading-relaxed">
                {prediction.description}
              </span>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <LoadingSpinner className="w-5 h-5" />
        </div>
      )}

      {!isLoaded && !loadError && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-lg">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Cargando buscador de direcciones...
          </div>
        </div>
      )}
    </div>
  );
};
